import { useState } from 'react';
import { useUser } from '../context/UserContext';
import { INTERESTS } from '../types/user';
import { ThemeSwitcher } from './ThemeSwitcher';
import { InvisibleToggle } from './social/InvisibleToggle';
import { SocialConsentSection } from './economy';
import './PreferencesPanel.css';

export function PreferencesPanel() {
  const { user, updatePreferences } = useUser();

  const [prefs, setPrefs] = useState({
    minAge: user?.preferences.minAge || 18,
    maxAge: user?.preferences.maxAge || 45,
    interests: user?.preferences.interests || [],
    showOnlineOnly: user?.preferences.showOnlineOnly || false,
  });

  const [saved, setSaved] = useState(false);

  const toggleInterest = (interest: string) => {
    setPrefs(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
    setSaved(false);
  };

  const handleSave = () => {
    updatePreferences(prefs);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!user) return null;

  return (
    <div className="preferences-panel">
      <div className="panel-header">
        <h1>Search Preferences</h1>
        <p>Customize your matching criteria</p>
      </div>

      <div className="preferences-card">
        <section className="pref-section">
          <ThemeSwitcher />
        </section>

        <section className="pref-section">
          <h3>Age Range</h3>
          <p className="section-desc">Find people within this age range</p>
          <div className="age-slider-container">
            <div className="age-inputs">
              <div className="age-input-group">
                <label>Min Age</label>
                <input
                  type="number"
                  value={prefs.minAge}
                  onChange={(e) => {
                    setPrefs(p => ({ ...p, minAge: parseInt(e.target.value) || 18 }));
                    setSaved(false);
                  }}
                  min={18}
                  max={prefs.maxAge}
                />
              </div>
              <div className="age-range-display">
                {prefs.minAge} - {prefs.maxAge}
              </div>
              <div className="age-input-group">
                <label>Max Age</label>
                <input
                  type="number"
                  value={prefs.maxAge}
                  onChange={(e) => {
                    setPrefs(p => ({ ...p, maxAge: parseInt(e.target.value) || 99 }));
                    setSaved(false);
                  }}
                  min={prefs.minAge}
                  max={99}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="pref-section">
          <h3>Interests</h3>
          <p className="section-desc">Match with people who share these interests</p>
          <div className="interests-grid">
            {INTERESTS.map(interest => (
              <button
                key={interest}
                type="button"
                className={`interest-btn ${prefs.interests.includes(interest) ? 'selected' : ''}`}
                onClick={() => toggleInterest(interest)}
              >
                {interest}
              </button>
            ))}
          </div>
        </section>

        <section className="pref-section">
          <h3>Other Options</h3>
          <div className="toggle-option">
            <div className="toggle-info">
              <span className="toggle-label">Show online users only</span>
              <span className="toggle-desc">Only see users who are currently online</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={prefs.showOnlineOnly}
                onChange={(e) => {
                  setPrefs(p => ({ ...p, showOnlineOnly: e.target.checked }));
                  setSaved(false);
                }}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          {/* Invisible mode (social.dm_presence). Self-gates on the flag —
              renders nothing until Phase C is enabled. */}
          <InvisibleToggle />
        </section>

        {/* Effect-potion consent (Alchemist's Cove §6). Saves immediately on its own;
            self-gates on servers without the economy surface. */}
        <SocialConsentSection />

        <div className="save-section">
          <button className="save-btn" onClick={handleSave}>
            {saved ? '✓ Saved!' : 'Save Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
}
